import './i18n/config';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, useRef, lazy, Suspense } from "react";
import type { ReactNode } from "react";
import { trackPageView } from "./lib/analytics";
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from "./contexts/AuthContext";
import { AccessibilityProvider } from "./components/compliance/AccessibilityProvider";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import GlobalOptimizations from "./components/GlobalOptimizations";
import MobileStickyCTA from "./components/MobileStickyCTA";

// Landing page loads eagerly so first-paint on / isn't gated on a chunk.
import LightStudioHome from "./pages/LightStudioHome";

// All other routes are code-split.
const ForBusinesses = lazy(() => import("./pages/ForBusinesses"));
const EscapeYourJob = lazy(() => import("./pages/EscapeYourJob"));
const ServicesOverview = lazy(() => import("./pages/services/ServicesOverview"));
const AIWebsites = lazy(() => import("./pages/services/AIWebsites"));
const AIChatbots = lazy(() => import("./pages/services/AIChatbots"));
const CRMAutomation = lazy(() => import("./pages/services/CRMAutomation"));
const WorkflowAutomation = lazy(() => import("./pages/services/WorkflowAutomation"));
const AIAudit = lazy(() => import("./pages/services/AIAudit"));
const VoiceAI = lazy(() => import("./pages/services/VoiceAI"));
const AIAgents = lazy(() => import("./pages/services/AIAgents"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const CaseStudyDetail = lazy(() => import("./pages/CaseStudyDetail"));
const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Work = lazy(() => import("./pages/Work"));
const AIReadiness = lazy(() => import("./pages/AIReadiness"));
const ROICalculator = lazy(() => import("./pages/ROICalculator"));
const Funding = lazy(() => import("./pages/Funding"));
const Pricing = lazy(() => import("./pages/Pricing"));
const PricingComparison = lazy(() => import("./pages/PricingComparison"));
const MarketingPackages = lazy(() => import("./pages/MarketingPackages"));
const SEOEdmonton = lazy(() => import("./pages/SEOEdmonton"));
const Partners = lazy(() => import("./pages/Partners"));
const Resources = lazy(() => import("./pages/Resources"));
const Blog = lazy(() => import("./pages/Blog"));
const NewAbout = lazy(() => import("./pages/NewAbout"));
const NewContact = lazy(() => import("./pages/NewContact"));
const Auth = lazy(() => import("./pages/Auth"));
const Industries = lazy(() => import("./pages/Industries"));
const IndustryDetail = lazy(() => import("./pages/IndustryDetail"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Careers = lazy(() => import("./pages/Careers"));
const CareersApply = lazy(() => import("./pages/CareersApply"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const AccessibilityStatement = lazy(() => import("./pages/AccessibilityStatement"));
const DataRequest = lazy(() => import("./pages/DataRequest"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const ExportData = lazy(() => import("./pages/ExportData"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Admin = lazy(() => import("./pages/Admin"));
const Research = lazy(() => import("./pages/Research"));
const SubscriptionDemo = lazy(() => import("./pages/SubscriptionDemo"));
const TrustpilotAI = lazy(() => import("./pages/TrustpilotAI"));
const Success = lazy(() => import("./pages/Success"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const QualifyLead = lazy(() => import("./pages/QualifyLead"));
const EdmontonAI = lazy(() => import("./pages/city-locations/EdmontonAI"));
const TorontoRealEstateAI = lazy(() => import("./pages/city-locations/TorontoRealEstateAI"));
const OntarioHealthcareAI = lazy(() => import("./pages/city-locations/OntarioHealthcareAI"));
const CalgaryAI = lazy(() => import("./pages/city-locations/CalgaryAI"));
const VancouverAI = lazy(() => import("./pages/city-locations/VancouverAI"));
const LangleyAI = lazy(() => import("./pages/city-locations/LangleyAI"));
const CanadaAI = lazy(() => import("./pages/CanadaAI"));
const ClientDashboard = lazy(() => import("./pages/ClientDashboard"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

interface RouteDef {
  path: string;
  element: ReactNode;
  /**
   * If false, don't mirror this route under /fr/. Use for utility/admin
   * routes that have no user-facing reason to exist in French.
   */
  localize?: boolean;
}

// Single source of truth for every app route. Each entry is rendered
// twice: once at `path` (English), once at `/fr${path}` (French). The
// same React element is reused for both so there's no double bundle load.
const ROUTES: RouteDef[] = [
  { path: '/', element: <LightStudioHome /> },
  { path: '/for-businesses', element: <ForBusinesses /> },
  { path: '/escape-your-job', element: <EscapeYourJob /> },
  { path: '/services', element: <ServicesOverview /> },
  { path: '/services/ai-websites', element: <AIWebsites /> },
  { path: '/services/ai-chatbots', element: <AIChatbots /> },
  { path: '/services/crm-automation', element: <CRMAutomation /> },
  { path: '/services/workflow-automation', element: <WorkflowAutomation /> },
  { path: '/services/ai-audit', element: <AIAudit /> },
  { path: '/services/ai-voice-agents', element: <VoiceAI /> },
  { path: '/services/ai-agents', element: <AIAgents /> },
  // Redirects from the old consulting service slugs → closest package service
  { path: '/services/ai-strategy', element: <Navigate to="/services/ai-audit" replace />, localize: false },
  { path: '/services/generative-ai', element: <Navigate to="/services/ai-chatbots" replace />, localize: false },
  { path: '/services/machine-learning', element: <Navigate to="/services/crm-automation" replace />, localize: false },
  { path: '/services/data-science', element: <Navigate to="/services/ai-websites" replace />, localize: false },
  { path: '/services/implementation-support', element: <Navigate to="/services/workflow-automation" replace />, localize: false },
  { path: '/case-studies', element: <CaseStudies /> },
  { path: '/case-studies/:slug', element: <CaseStudyDetail /> },
  { path: '/products', element: <Products /> },
  { path: '/products/:slug', element: <ProductDetail /> },
  { path: '/work', element: <Work /> },
  { path: '/ai-readiness', element: <AIReadiness /> },
  { path: '/roi-calculator', element: <ROICalculator /> },
  { path: '/cdap', element: <Funding /> },
  { path: '/pricing', element: <Pricing /> },
  { path: '/marketing-packages', element: <MarketingPackages /> },
  { path: '/seo-edmonton', element: <SEOEdmonton />, localize: false },
  { path: '/partners', element: <Partners /> },
  { path: '/pricing-comparison', element: <PricingComparison /> },
  { path: '/resources', element: <Resources /> },
  { path: '/blog', element: <Blog /> },
  // Superseded by the refreshed package-model city posts — redirect (consolidates SEO).
  { path: '/blog/calgary-ai-consulting-energy-realestate', element: <Navigate to="/blog/ai-websites-automation-calgary" replace />, localize: false },
  { path: '/blog/vancouver-ai-services-bc-privacy-tech', element: <Navigate to="/blog/ai-websites-automation-vancouver" replace />, localize: false },
  { path: '/blog/:slug', element: <BlogPost /> },
  { path: '/about', element: <NewAbout /> },
  { path: '/contact', element: <NewContact /> },
  { path: '/auth', element: <Auth />, localize: false },
  { path: '/industries', element: <Industries /> },
  { path: '/industries/:slug', element: <IndustryDetail /> },
  { path: '/faq', element: <FAQ /> },
  { path: '/careers', element: <Careers /> },
  { path: '/careers/apply', element: <CareersApply /> },
  { path: '/careers/apply/:jobSlug', element: <CareersApply /> },
  { path: '/apply', element: <CareersApply /> },
  { path: '/privacy-policy', element: <PrivacyPolicy /> },
  { path: '/terms-of-service', element: <TermsOfService /> },
  { path: '/accessibility', element: <AccessibilityStatement /> },
  { path: '/data-request', element: <DataRequest /> },
  { path: '/unsubscribe', element: <Unsubscribe /> },
  { path: '/export-data', element: <ExportData /> },
  { path: '/cookie-policy', element: <CookiePolicy /> },
  { path: '/admin', element: <ProtectedRoute><Admin /></ProtectedRoute>, localize: false },
  { path: '/research', element: <Research />, localize: false },
  { path: '/subscription-demo', element: <SubscriptionDemo />, localize: false },
  { path: '/trustpilot-ai', element: <TrustpilotAI /> },
  { path: '/success', element: <Success />, localize: false },
  { path: '/thank-you', element: <ThankYou />, localize: false },
  { path: '/qualify', element: <QualifyLead />, localize: false },
  { path: '/ai-consulting-edmonton', element: <EdmontonAI /> },
  { path: '/real-estate-ai-toronto', element: <TorontoRealEstateAI /> },
  { path: '/ai-for-healthcare-ontario', element: <OntarioHealthcareAI /> },
  { path: '/ai-services-calgary', element: <CalgaryAI /> },
  { path: '/ai-consulting-vancouver', element: <VancouverAI /> },
  { path: '/web-design-langley', element: <LangleyAI /> },
  { path: '/ai-consulting-canada', element: <CanadaAI /> },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <ClientDashboard />
      </ProtectedRoute>
    ),
    localize: false,
  },
];

const frenchPathFor = (path: string) => (path === '/' ? '/fr' : `/fr${path}`);

// SPA Route Tracking Wrapper
const RouteTracker = () => {
  const location = useLocation();
  const isFirstLoad = useRef(true);
  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);
  return null;
};

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center" aria-busy="true" aria-live="polite">
    <div className="h-8 w-8 rounded-full border-2 border-border border-t-primary animate-spin" aria-hidden="true" />
    <span className="sr-only">Loading page…</span>
  </div>
);

// Wrapper that conditionally applies Layout based on route
const ConditionalLayout = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const isHomepage = location.pathname === '/' || location.pathname === '/fr';

  if (isHomepage) {
    return <>{children}</>;
  }

  return <Layout>{children}</Layout>;
};

const App = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AccessibilityProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <RouteTracker />
                <ScrollToTop />
                <GlobalOptimizations />
                <ConditionalLayout>
                  <Suspense fallback={<RouteFallback />}>
                    <Routes>
                      {ROUTES.map((r) => (
                        <Route key={`en-${r.path}`} path={r.path} element={r.element} />
                      ))}
                      {ROUTES.filter((r) => r.localize !== false).map((r) => (
                        <Route
                          key={`fr-${r.path}`}
                          path={frenchPathFor(r.path)}
                          element={r.element}
                        />
                      ))}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </ConditionalLayout>
                <MobileStickyCTA />
              </BrowserRouter>
            </TooltipProvider>
          </AccessibilityProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
